import clsx from 'clsx';
import { formatInt } from '@/lib/format';
import { Card } from './Card';
import { Sparkline } from './Sparkline';

export type KpiTileProps = {
  label: string;
  value: number | string;
  delta?: number;
  spark?: number[];
  loading?: boolean;
  /** Native browser tooltip on the whole tile (hover to read). */
  tooltip?: string;
};

function formatDelta(d: number) {
  const pct = (d * 100).toFixed(1);
  const sign = d > 0 ? '+' : '';
  return `${sign}${pct}%`;
}

export function KpiTile({ label, value, delta, spark, loading, tooltip }: KpiTileProps) {
  return (
    <Card
      padding="md"
      className={tooltip ? 'flex cursor-help flex-col justify-between' : 'flex flex-col justify-between'}
      title={tooltip}
    >
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        {loading ? (
          <div className="mt-2 h-9 w-24 animate-pulse rounded-lg bg-gray-200/80" />
        ) : (
          <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
            {typeof value === 'number' ? formatInt(value) : value}
          </p>
        )}
        {!loading && delta !== undefined && (
          <p
            className={clsx(
              'mt-1 text-xs font-medium',
              delta > 0 && 'text-emerald-600',
              delta < 0 && 'text-red-600',
              delta === 0 && 'text-gray-500'
            )}
          >
            {formatDelta(delta)} vs prior period
          </p>
        )}
      </div>
      {!loading && spark && spark.length > 0 && (
        <div className="mt-3">
          <Sparkline data={spark} ariaLabel={`${label} trend`} />
        </div>
      )}
    </Card>
  );
}
